import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrudTableProps } from './CrudTable.types';
import { CrudTable } from './CrudTable';
import { useTableColumns } from '../../../hooks/knowledgeBase/useTableColumns';
import { SynonymsEditDialog } from '../SynonymsEditDialog/SynonymsEditDialog';
import type { SynonymsEditTarget } from '../SynonymsEditDialog/types';

export const CrudTableContainer = (props: CrudTableProps) => {
  const { t } = useTranslation('componentSchema');

  const {
    config,
    data,
    loading,
    error,
    editingRows,
    onAddRow,
    onEditRow,
    onDeleteRow,
    onSaveRow,
    onCancelEdit,
    onRefresh,
    associationCounts,
    associationLabel,
    onOpenTransfer,
    associationCountsSecondary,
    associationLabelSecondary,
    onOpenTransferSecondary,
    onApplySynonyms,
    pagination,
    onPageChange,
    search,
    onSearch,
  } = props;

  const [searchValue, setSearchValue] = useState<string>(search ?? '');
  const [prevSearch, setPrevSearch] = useState<string | undefined>(search);
  const [synonymsTarget, setSynonymsTarget] =
    useState<SynonymsEditTarget | null>(null);

  if (search !== prevSearch) {
    setSearchValue(search ?? '');
    setPrevSearch(search);
  }

  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    const timerId = debounceRef.current;

    return () => {
      if (timerId) window.clearTimeout(timerId);
    };
  }, []);

  const isEditing = (key: string) => key in editingRows;
  const displayData = useMemo(() => {
    const newRows = Object.entries(editingRows)
      .filter(([key]) => key.startsWith('row_'))
      .map(([key, row]) => ({ ...row, _key: key }));

    return [...newRows, ...data];
  }, [data, editingRows]);

  const handleOpenSynonymsEdit = useCallback(
    (rowKey: string, title: string, synonyms: string[]) => {
      setSynonymsTarget({ rowKey, title, synonyms });
    },
    [],
  );

  const handleApplySynonyms = useCallback(
    async (synonyms: string[]) => {
      if (!synonymsTarget || !onApplySynonyms) return;
      await onApplySynonyms(synonymsTarget.rowKey, synonyms);
    },
    [onApplySynonyms, synonymsTarget],
  );

  const tableColumns = useTableColumns({
    config,
    isEditing,
    editingRows,
    onEditRow,
    onSaveRow,
    onCancelEdit,
    onDeleteRow,
    associationCounts,
    associationLabel,
    onOpenTransfer,
    associationCountsSecondary,
    associationLabelSecondary,
    onOpenTransferSecondary,
    onOpenSynonymsEdit: onApplySynonyms ? handleOpenSynonymsEdit : undefined,
    t,
  });

  return (
    <>
      <CrudTable
        config={config}
        data={displayData}
        loading={loading}
        error={error}
        editingRows={editingRows}
        onAddRow={onAddRow}
        onEditRow={onEditRow}
        onDeleteRow={onDeleteRow}
        onSaveRow={onSaveRow}
        onCancelEdit={onCancelEdit}
        onRefresh={onRefresh}
        tableColumns={tableColumns}
        pagination={pagination}
        onPageChange={onPageChange}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        onSearch={onSearch}
        debounceRef={debounceRef}
      />
      <SynonymsEditDialog
        key={synonymsTarget?.rowKey ?? 'closed'}
        open={!!synonymsTarget}
        title={synonymsTarget?.title ?? ''}
        initialSynonyms={synonymsTarget?.synonyms ?? []}
        onApply={handleApplySynonyms}
        onClose={() => setSynonymsTarget(null)}
      />
    </>
  );
};
